import { Directory, File, Paths } from "@-/com.npmjs.expo-file-system";
import onetime from "@-/com.npmjs.onetime";
import { databaseLifecycle } from "@-/dev.improve.haniverse.modules.database-lifecycle";
import { DatabaseGenerations } from "#database-generations";
import { resolveSqlitePath, toFileUri } from "#resolve-sqlite-path";

/**
 * Resolved lazily, NOT at module scope.
 *
 * `resolveSqlitePath` reads expo-sqlite's `defaultDatabaseDirectory`, which is `undefined` wherever
 * the native module is absent. The web export's SSR prerender bundles with
 * `resolver.environment=node`, so it takes the `default` (native) side of every platform-split
 * export and evaluates this module even though nothing on web calls into it. A module-scope call
 * therefore ran at import time and killed `expo export --platform web` outright:
 *
 *   Metro error: Cannot read properties of undefined (reading 'startsWith')
 *     at e.resolveSqlitePath
 *
 * That failed `web-preview-deploy`, which every maestro shard gates on, so the whole Android suite
 * was skipped (run 35764113666). Deferring the read keeps this module import-safe everywhere while
 * behaving identically on native, where the first accessor call resolves the same path once.
 */
const getRoot = onetime(() => resolveSqlitePath(""));
const uriFor = (path: string) => toFileUri(resolveSqlitePath(path));
export const nativeDatabaseGenerations = new DatabaseGenerations({
  acquire: (name) => databaseLifecycle.acquire(getRoot(), name),
  allocate: () => databaseLifecycle.allocate(getRoot()),
  compareAndSwap: (previous, next) => databaseLifecycle.compareAndSwap(getRoot(), previous, next),
  exists: (path) => new File(uriFor(path)).exists,
  listGenerations: () => {
    const parent = new Directory(uriFor(".haniverse-generations"));
    if (!parent.exists) {
      return [];
    }
    return parent
      .list()
      .filter((entry) => entry instanceof Directory)
      .map((entry) => `.haniverse-generations/${entry.name}`);
  },
  read: () => databaseLifecycle.read(getRoot()),
  remove: (path) => {
    const uri = uriFor(path);
    const info = Paths.info(uri);
    if (!info.exists) {
      return;
    }
    if (info.isDirectory === true) {
      new Directory(uri).delete();
    } else {
      new File(uri).delete();
    }
  },
});

export function sealDatabaseGeneration(path: string): void {
  databaseLifecycle.seal(getRoot(), path);
}

export function retireNativeDatabases(names: readonly string[]): void {
  nativeDatabaseGenerations.retire(names);
}

/** Clean-install reset of these logical databases; see `DatabaseGenerations.purge`. */
export function purgeNativeDatabases(names: readonly string[]): string[] {
  return nativeDatabaseGenerations.purge(names);
}
